import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";
import { getActiveMaintenanceBanner } from "@/api/notifications";
import { alertSeverityBannerStyles } from "@/features/admin/lib/badges";
import { Badge } from "@/components/ui/Badge";

/**
 * How often the tenant shells re-check the maintenance banner. Five minutes is a
 * sensible cadence for a platform-wide announcement — frequent enough that a
 * banner raised mid-session appears without a reload, but far from "hammering"
 * the endpoint. `staleTime` matches the interval so background refetches on
 * focus/mount within the window are served from cache.
 */
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * MaintenanceBanner — a prominent top banner shown in the tenant (dispatcher)
 * shell when the platform maintenance banner is active (TASK-3931).
 *
 * Self-contained: it fetches `GET /notifications/maintenance-banner/` (any
 * authenticated user) and renders nothing when `active` is false. It is
 * dismissible per session — the dismissal is scoped to the exact message text,
 * so a NEW announcement re-appears even after an earlier one was dismissed.
 *
 * Rendered only inside the dispatcher AppShell, so the admin / dealer shells are
 * unaffected (they never mount this component).
 */
export function MaintenanceBanner() {
  const { t } = useTranslation();
  const [dismissedMessage, setDismissedMessage] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["maintenance-banner"],
    queryFn: getActiveMaintenanceBanner,
    // Re-check periodically so a banner raised mid-session shows without reload.
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: REFETCH_INTERVAL_MS,
  });

  if (!data?.active || !data.message) {
    return null;
  }

  // Per-session dismissal keyed on the message text: dismissing one banner does
  // not suppress a later, different announcement.
  if (dismissedMessage === data.message) {
    return null;
  }

  // Color the banner by severity (info/warning/critical), reusing the shared
  // severity color language so the banner matches the broadcast bell. `critical`
  // (red) is visually distinct from the previous amber default.
  const styles = alertSeverityBannerStyles(data.severity);

  return (
    <div
      role="status"
      className={`flex items-start gap-3 border-b px-4 py-3 lg:px-6 ${styles.container}`}
    >
      <AlertTriangle
        aria-hidden="true"
        size={18}
        className={`mt-0.5 shrink-0 ${styles.icon}`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Badge
          variant="planned"
          label={t("notifications.systemBadge")}
          className="self-start"
        />
        <p className={`min-w-0 text-sm font-medium ${styles.text}`}>
          {data.message}
        </p>
      </div>
      <button
        type="button"
        aria-label={t("maintenanceBanner.dismiss")}
        onClick={() => setDismissedMessage(data.message)}
        className={`-mr-1 shrink-0 rounded-lg p-1 transition-colors ${styles.button}`}
      >
        <X size={16} />
      </button>
    </div>
  );
}
